import { useQuery } from '@tanstack/react-query';
import { listMessages, type ListMessagesResponse } from '../services/dmMessages';
import {
    DM_MESSAGES_CACHE_TTL_MS,
    readCachedDmMessages,
    writeCachedDmMessages
} from './dmMessagesCache';

// Signed attachment URLs live 1h; refetch well before that while the thread stays open.
const DM_MESSAGES_REFETCH_INTERVAL_MS = 30 * 60 * 1000;

// DM is slow to answer: every visit shows the last thread at once (from memory, or from the
// session after a reload) and refetches it in the background.
export function useDmMessages(patientId: string | undefined) {
    const { data, error, isLoading, isFetching, refetch } = useQuery<ListMessagesResponse>({
        queryKey: ['v2/patients', patientId, 'dm', 'messages'],
        queryFn: async () => {
            const fresh = await listMessages(patientId!);
            writeCachedDmMessages(patientId!, fresh);
            return fresh;
        },
        enabled: !!patientId,
        initialData: () => (patientId ? readCachedDmMessages(patientId)?.data : undefined),
        initialDataUpdatedAt: () => (patientId ? readCachedDmMessages(patientId)?.at : undefined),
        staleTime: 0,
        gcTime: DM_MESSAGES_CACHE_TTL_MS,
        refetchInterval: DM_MESSAGES_REFETCH_INTERVAL_MS
    });

    return {
        data: data ?? null,
        isLoading,
        /** A background refresh while cached messages are already shown. */
        isRefreshing: isFetching && !isLoading,
        error,
        refetch
    };
}
